import { LitElement, html, nothing } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import "@phosphor-icons/webcomponents/PhSmiley";
import "@phosphor-icons/webcomponents/PhPaperPlaneTilt";
import "@phosphor-icons/webcomponents/PhPaperclip";
import "@phosphor-icons/webcomponents/PhX";
import { styles } from "./chat-message-input.css";
import { uploadFile, FileUploadError, formatUploadError } from "ui-common/services/file-upload";
import { formatFileSize, CHAT_ATTACHMENT_MIME_TYPES } from "helpers/fileValidation";

const CHAT_ATTACHMENT_ACCEPT = CHAT_ATTACHMENT_MIME_TYPES.join(",");

interface PendingAttachment {
  tempId: string;
  file: File;
  progress: number;
  fileId?: string;
  error?: string;
}

export interface SendMessageAttachment {
  fileId: string;
  originalName: string;
  sizeBytes: number;
}

/**
 * Chat message input with avatar, emoji, send, and optional file attachments.
 *
 * When `allow-attachments` is set, a paperclip button lets the user select
 * files that upload immediately and appear as removable chips above the
 * textarea. On send, only successfully uploaded attachments are emitted.
 *
 * @fires send-message - { message, notifyByEmail, attachments }
 * @fires emoji-toggle
 */
@customElement("chat-message-input")
export class ChatMessageInput extends LitElement {
  static override styles = styles;

  @query("textarea") private textareaElement!: HTMLTextAreaElement;
  @query("input[type=file]") private fileInputElement?: HTMLInputElement;

  @property({ type: String }) placeholder = "Send a message...";
  @property({ type: String, attribute: "avatar-url" }) avatarUrl?: string;
  @property({ type: Boolean }) disabled = false;
  @property({ type: Boolean }) sending = false;
  @property({ type: Boolean, attribute: "show-notify-toggle" }) showNotifyToggle = false;
  @property({ type: String, attribute: "notify-label" }) notifyLabel = "Notify fans via email";
  @property({ type: Boolean, attribute: "allow-attachments" }) allowAttachments = false;

  @state() private value = "";
  @state() private notifyByEmail = false;
  @state() private isSingleLine = true;
  @state() private pendingAttachments: PendingAttachment[] = [];

  private static readonly MAX_LENGTH = 1000;
  private static readonly COUNTER_THRESHOLD = 50;

  private get charCount(): number {
    return this.value.length;
  }

  private get isOverLimit(): boolean {
    return this.charCount > ChatMessageInput.MAX_LENGTH;
  }

  private get showCharCounter(): boolean {
    return this.charCount > ChatMessageInput.MAX_LENGTH - ChatMessageInput.COUNTER_THRESHOLD;
  }

  private get readyAttachments(): PendingAttachment[] {
    return this.pendingAttachments.filter((a) => a.fileId && !a.error);
  }

  private get uploadsInFlight(): boolean {
    return this.pendingAttachments.some((a) => !a.fileId && !a.error);
  }

  private get canSend(): boolean {
    const hasText = this.value.trim().length > 0;
    const hasReadyAttachment = this.readyAttachments.length > 0;
    return (
      (hasText || hasReadyAttachment) && !this.uploadsInFlight && !this.isOverLimit && !this.disabled && !this.sending
    );
  }

  private handleInput(e: Event): void {
    const textarea = e.target as HTMLTextAreaElement;
    this.value = textarea.value;
    this.autoResize();
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      this.send();
    }
  }

  private autoResize(): void {
    const textarea = this.textareaElement;
    if (!textarea) return;
    textarea.style.height = "auto";
    const scrollHeight = textarea.scrollHeight;
    this.isSingleLine = scrollHeight <= 28;
    textarea.style.height = `${scrollHeight}px`;
  }

  private send(): void {
    if (!this.canSend) return;

    const message = this.value.trim();
    const notifyByEmail = this.notifyByEmail;
    const attachments: SendMessageAttachment[] = this.readyAttachments.map((a) => ({
      fileId: a.fileId!,
      originalName: a.file.name,
      sizeBytes: a.file.size,
    }));

    const textarea = this.textareaElement;
    if (textarea) {
      textarea.value = "";
    }

    this.dispatchEvent(
      new CustomEvent("send-message", {
        detail: { message, notifyByEmail, attachments },
        bubbles: true,
        composed: true,
      }),
    );

    this.value = "";
    this.isSingleLine = true;
    this.notifyByEmail = false;
    this.pendingAttachments = [];

    this.updateComplete.then(() => {
      if (this.textareaElement) {
        this.textareaElement.style.height = "auto";
        this.textareaElement.focus();
      }
    });
  }

  private handleEmojiClick(): void {
    this.dispatchEvent(
      new CustomEvent("emoji-toggle", {
        bubbles: true,
        composed: true,
      }),
    );
  }

  private handleAttachClick(): void {
    this.fileInputElement?.click();
  }

  private async handleFileSelect(e: Event): Promise<void> {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = "";
    for (const file of files) {
      void this.uploadAttachment(file);
    }
  }

  private async uploadAttachment(file: File): Promise<void> {
    const tempId = `att-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.pendingAttachments = [...this.pendingAttachments, { tempId, file, progress: 0 }];

    try {
      const result = await uploadFile(file, "chat_attachment", {
        onProgress: (progress) => {
          this.pendingAttachments = this.pendingAttachments.map((a) => (a.tempId === tempId ? { ...a, progress } : a));
        },
      });
      this.pendingAttachments = this.pendingAttachments.map((a) =>
        a.tempId === tempId ? { ...a, fileId: result.fileId, progress: 100 } : a,
      );
    } catch (err) {
      const message = err instanceof FileUploadError ? formatUploadError(err) : "Upload failed";
      this.pendingAttachments = this.pendingAttachments.map((a) =>
        a.tempId === tempId ? { ...a, error: message } : a,
      );
    }
  }

  private removeAttachment(tempId: string): void {
    this.pendingAttachments = this.pendingAttachments.filter((a) => a.tempId !== tempId);
  }

  public override focus(): void {
    this.textareaElement?.focus();
  }

  public clear(): void {
    this.value = "";
    this.isSingleLine = true;
    this.pendingAttachments = [];
    if (this.textareaElement) {
      this.textareaElement.style.height = "auto";
    }
  }

  public insertText(text: string): void {
    this.value += text;
    this.updateComplete.then(() => {
      this.autoResize();
      this.focus();
    });
  }

  private handleNotifyToggle(e: Event): void {
    this.notifyByEmail = (e.target as HTMLInputElement).checked;
  }

  private renderAttachmentChips() {
    if (this.pendingAttachments.length === 0) return nothing;
    return html`
      <ul class="attachment-chips" aria-live="polite">
        ${this.pendingAttachments.map(
          (a) => html`
            <li
              class=${classMap({
                chip: true,
                uploading: !a.fileId && !a.error,
                error: Boolean(a.error),
              })}
            >
              <span class="chip-name" title=${a.file.name}>${a.file.name}</span>
              <span class="chip-meta">
                ${a.error ? a.error : a.fileId ? formatFileSize(a.file.size) : `${Math.round(a.progress)}%`}
              </span>
              <button
                class="chip-remove"
                type="button"
                @click=${() => this.removeAttachment(a.tempId)}
                aria-label="Remove attachment"
              >
                <ph-x size="14" weight="bold"></ph-x>
              </button>
            </li>
          `,
        )}
      </ul>
    `;
  }

  override render() {
    return html`
      <div class="input-wrapper">
        ${this.renderAttachmentChips()}
        <div
          class=${classMap({
            container: true,
            "single-line": this.isSingleLine,
          })}
        >
          ${this.avatarUrl ? html`<img class="avatar" src=${this.avatarUrl} alt="" />` : nothing}

          <textarea
            class="message-input"
            .value=${this.value}
            placeholder=${this.placeholder}
            ?disabled=${this.disabled || this.sending}
            @input=${this.handleInput}
            @keydown=${this.handleKeyDown}
            rows="1"
          ></textarea>

          <div class="button-group">
            ${this.allowAttachments
              ? html`
                  <button
                    class="attach-button"
                    type="button"
                    ?disabled=${this.disabled || this.sending}
                    @click=${this.handleAttachClick}
                    aria-label="Attach a file"
                  >
                    <ph-paperclip size="20" weight="bold"></ph-paperclip>
                  </button>
                  <input
                    type="file"
                    hidden
                    multiple
                    accept=${CHAT_ATTACHMENT_ACCEPT}
                    @change=${this.handleFileSelect}
                  />
                `
              : nothing}

            <button
              class="send-button"
              type="button"
              ?disabled=${!this.canSend}
              @click=${this.send}
              aria-label="Send message"
            >
              <ph-paper-plane-tilt size="20" weight="fill"></ph-paper-plane-tilt>
            </button>

            <button
              class="emoji-button"
              type="button"
              ?disabled=${this.disabled || this.sending}
              @click=${this.handleEmojiClick}
              aria-label="Open emoji picker"
            >
              <ph-smiley size="20" weight="bold"></ph-smiley>
            </button>
          </div>
        </div>
        ${this.showCharCounter
          ? html`<span class=${classMap({ "char-counter": true, "over-limit": this.isOverLimit })}
              >${this.charCount}/${ChatMessageInput.MAX_LENGTH}</span
            >`
          : nothing}
        ${this.showNotifyToggle
          ? html`
              <label class="notify-toggle">
                <input type="checkbox" .checked=${this.notifyByEmail} @change=${this.handleNotifyToggle} />
                <span class="notify-label">${this.notifyLabel}</span>
              </label>
            `
          : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "chat-message-input": ChatMessageInput;
  }
}
